import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { SlotBlockedNotice } from './SlotBlockedNotice';

/* Department-specific landing pages — go directly instead of bouncing through /dashboard */
const DEPT_HOME: Record<string, string> = {
  Manufacturing: '/manufacturing/dashboard',
  'Inventory & Warehouse': '/warehouse/dashboard',
  Inventory: '/warehouse/dashboard',
};

function getDashboardForUser(u: any): string {
  // Role-strict landings first (May 26, 2026).
  // Lead Triage Coordinator (Deepika) — never lands on /dashboard; strict view.
  if (u?.role?.name === 'lead_triage_coordinator') return '/leads/triage';
  const deptName = u?.department?.name;
  return (deptName && DEPT_HOME[deptName]) || '/dashboard';
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, isLoading, user, clearError, slotBlock, clearSlotBlock } = useAuthStore();
  const navigate = useNavigate();

  /*
    🔒 THE BLOCK SCREEN MUST NOT OUTLIVE THE PAGE. It is held in the store, not
    in this component, so /login → blocked → /forgot-password → back brings it
    straight back — but `email` and `password` are fresh empty strings by then,
    so the takeover button would re-post BLANK credentials and the server would
    answer 401. Clear it on the way out.
  */
  useEffect(() => () => clearSlotBlock(), [clearSlotBlock]);

  /* 🔒 A SECOND 409 CHANGES NOTHING ON SCREEN. The store re-sets an identical
     `slotBlock`, React renders the same tree, and the person is left pressing a
     button at a page that looks frozen. Say it out loud instead. */
  const [takeoverRefused, setTakeoverRefused] = useState(false);

  /*
    🔒 THE OIDC REFUSAL LANDS HERE, AND IT CARRIES NOTHING BUT A CHANNEL.
    A single-sign-on callback is a browser NAVIGATION, not a fetch — a 409 JSON
    body would render as raw text — so core/auth/oidc.routes.ts redirects a
    refused person to /login?slot_taken=<channel>. Without this the refusal
    lands on a silent login page and the person is told nothing at all. Saying
    it plainly here gets them to type their password, which runs the normal 409
    flow, which is the one that knows WHICH device is holding the slot and can
    offer the takeover.
  */
  const [searchParams] = useSearchParams();
  const blockedChannel = searchParams.get('slot_taken');
  const blockedWord = blockedChannel === 'apk' ? 'phone' : blockedChannel === 'web' ? 'browser' : null;

  // If already logged in, redirect to department-appropriate dashboard
  useEffect(() => {
    if (user && !isLoading) {
      navigate(getDashboardForUser(user), { replace: true });
    }
  }, [user, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      // Use window.location for a clean navigation (avoids React Router race conditions)
      const u = useAuthStore.getState().user;
      window.location.href = getDashboardForUser(u);
    } catch {}
  };

  return (
    <div
      className="aura-root flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--ivory-0)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <img
            src="/veda-agro-logo.png"
            alt="Veda Agro"
            className="mx-auto h-24 w-auto object-contain"
          />
          <h1
            className="mt-4 text-2xl"
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 600,
              color: 'var(--ink-0)',
              letterSpacing: '-0.01em',
            }}
          >
            Sign in to AURA
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
            AI Unified Resource Architecture
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-8"
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            boxShadow: 'var(--sh-2)',
          }}
        >
          {slotBlock && (
            <>
              <SlotBlockedNotice
                block={slotBlock}
                busy={isLoading}
                onTakeover={() => {
                  setTakeoverRefused(false);
                  void login(email, password, true).catch((err: any) => {
                    if (err?.response?.status === 409) setTakeoverRefused(true);
                  });
                }}
              />
              {takeoverRefused && (
                <p className="mb-4 text-sm" style={{ color: 'var(--danger, #c34a48)' }}>
                  That device is still holding the slot — try again.
                </p>
              )}
            </>
          )}

          {!slotBlock && blockedWord && (
            <div
              className="mb-4 rounded-lg border p-3 text-sm"
              style={{ borderColor: '#efc15d', background: '#fff8df', color: '#6b5320' }}
            >
              <p className="font-semibold">Your account is already open on another {blockedWord}.</p>
              <p className="mt-1 text-xs">
                Sign in again below and we will show you which {blockedWord} it is, so you can sign
                that one out and carry on here.
              </p>
            </div>
          )}

          {error && (
            <div
              className="mb-4 rounded-lg p-3 text-sm"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              {error}
              <button onClick={clearError} className="ml-2 font-medium underline">Dismiss</button>
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
              style={{
                background: 'var(--ivory-0)',
                color: 'var(--ink-0)',
                border: '1px solid var(--line)',
              }}
              placeholder="you@company.com"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium" style={{ color: 'var(--ink-1)' }}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none"
                style={{
                  background: 'var(--ivory-0)',
                  color: 'var(--ink-0)',
                  border: '1px solid var(--line)',
                }}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--ink-3)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
            style={{
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
            }}
            onMouseOver={(e) => {
              if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-1)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-0)';
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>

          {/*
            🔒 FORGOT PASSWORD HAD NO DOOR. The page and the endpoint have both
            been live and working since AI-vbof, but nothing on this screen
            linked to them, so the only way in was to type the URL. Manak,
            2026-09-04: "kaha hai bhai forgot password?"

            🔒 "SIGN UP" IS GONE, AND ON PURPOSE. It pointed at a registration
            endpoint that took no authentication and let the caller choose their
            own role — the security hole closed the same day. Accounts are made
            by an admin in Settings → Team. Leaving the link would advertise a
            door that now answers 403.
          */}
          <p className="mt-4 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            <Link to="/forgot-password" className="font-medium" style={{ color: 'var(--saffron-600)' }}>
              Forgot password?
            </Link>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">
          Powered by Ardhanex Tech Pvt Ltd
        </p>
      </div>
    </div>
  );
}
